/**
 * App-to-web JWT handoff for the CT partnership-linking flow.
 *
 * The TH app appends its ID token as a hash fragment (`#id_token=<jwt>`) when it
 * opens the RP Site. The external browser has no TH Cognito session, so that
 * token is read once, kept for the flow, and sent as `Authorization: Bearer` on
 * the partner-linking gateway mutations.
 *
 * A fragment rather than a query param because it never reaches servers, access
 * logs, or the `Referer` header. It is stripped from the URL immediately after
 * reading, and the raw token is never logged.
 *
 * It is persisted in `sessionStorage` because the OIDC round-trip drops the
 * fragment: the callback lands with only `?code=&state=`.
 */

import { AUTHORIZED_KEY } from 'state/graphql/links/auth-required-directive';
import { parseJwt } from 'utils/jwt';

const STORAGE_KEY = 'ct-partner-linking:app-token';
const HASH_PARAM = 'id_token';

/**
 * Shape check only: three non-empty segments whose payload decodes to a JSON
 * object. NOT a security check — an expired or wrong-`token_use` token still
 * passes. Its only job is to reject obviously broken handoffs before firing a
 * doomed gateway request. Signature, expiry, `token_use`, and audience remain
 * enforced by the loyalty-service authorizer.
 */
export function isWellFormedJwt(token: string | null | undefined): boolean {
  if (typeof token !== 'string' || token.length === 0) {
    return false;
  }
  const segments = token.split('.');
  if (segments.length !== 3 || segments.some(segment => segment.length === 0)) {
    return false;
  }
  const payload = parseJwt(token);
  return typeof payload === 'object' && payload !== null;
}

/**
 * True only when `exp` is present and already past. A missing or non-numeric
 * `exp` is unknown rather than expired, leaving the verdict to the authorizer.
 *
 * Expiry is the one claim the client must read, because it decides whether the
 * guest has an identity at all: a lapsed token authenticates nothing, so it
 * cannot stand in for a session, and none of its claims may gate anything.
 */
export function isExpiredJwt(token: string | null | undefined): boolean {
  if (typeof token !== 'string' || token.length === 0) {
    return false;
  }
  const exp = parseJwt(token)?.exp;
  return typeof exp === 'number' && exp * 1000 <= Date.now();
}

/** Reads the handoff token from the URL hash fragment, if present. */
export function readAppTokenFromUrl(): string | null {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) {
      return null;
    }
    const token = new URLSearchParams(hash).get(HASH_PARAM);
    return token || null;
  } catch {
    return null;
  }
}

/** Persists the handoff token for the duration of the linking flow. */
export function storeAppToken(token: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // sessionStorage unavailable (private mode / SSR) — non-fatal.
  }
}

/** Returns the persisted handoff token without removing it. */
export function getAppIdTokenFromSessionStorage(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The Cognito `sub` from the handoff token, for log correlation only.
 * App-origin guests have no Cognito session, so the generic logger's `userId` is
 * empty for them; this recovers the same id the gateway authorizes against.
 * Only ever exposes `sub` — never the raw token or other PII claims.
 */
export function getAppTokenUserId(
  token: string | null = getAppIdTokenFromSessionStorage()
): string | undefined {
  if (!token) {
    return undefined;
  }
  const payload = parseJwt(token);
  const sub = payload?.sub;
  return typeof sub === 'string' ? sub : undefined;
}

/** Removes the persisted handoff token once the flow is done. */
export function clearAppIdToken(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * True when a live handoff token exists in the URL hash or sessionStorage.
 *
 * The token stands in for a Cognito session at the auth gate, so a lapsed one
 * must not. It survives the OIDC round-trip and is kept on the block screens for
 * a retry, which leaves it in the tab long after it can authenticate anything;
 * past that point the guest belongs in the ordinary sign-in gate.
 */
export function hasAppToken(): boolean {
  const token = readAppTokenFromUrl() ?? getAppIdTokenFromSessionStorage();
  return token !== null && !isExpiredJwt(token);
}

/**
 * Removes the hash fragment from the address bar (and history entry) so the
 * raw token isn't left visible after we've captured it.
 */
export function stripTokenFromUrl(): void {
  try {
    const { pathname, search } = window.location;
    window.history.replaceState(null, '', `${pathname}${search}`);
  } catch {
    // no-op
  }
}

/**
 * Per-operation Apollo context that authenticates a gateway call as the handoff
 * token.
 *
 * `withAuthToken` merges `previousContext` over its own session header, so this
 * `authorization` wins even when the browser holds a session — that is what lets
 * an app-origin guest act as their token in a browser signed into a different
 * account. `AUTHORIZED_KEY` keeps the `@authRequired` link from cancelling the
 * request when there is no session at all.
 */
export function appTokenAuthContext(
  token: string
): {
  headers: { authorization: string };
  [AUTHORIZED_KEY]: boolean;
} {
  return {
    headers: { authorization: `Bearer ${token}` },
    [AUTHORIZED_KEY]: true,
  };
}
